'use client';

import React, { useState, Suspense } from 'react';
import logger from '@/lib/logger';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { Mail, Lock, User, ArrowRight, ArrowLeft, CheckCircle2, Phone } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { getSiteUrl } from '@/lib/auth/site-url';
import { EngageIQLogo } from '@/components/brand/EngageIQBrand';

function CitizenRegisterContent() {
    const router = useRouter();
    const searchParams = useSearchParams();

    // Read callId from URL params (e.g., /citizen/register?callId=abc123)
    const callId = searchParams.get('callId');
    const inviteToken = searchParams.get('inviteToken');
    const hasCallId = !!callId;
    const inviteSuffix = inviteToken ? `?inviteToken=${encodeURIComponent(inviteToken)}` : '';
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!email || !password) {
            setError('Email and password are required');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setIsLoading(true);

        try {
            const supabase = createClient();

            // Use centralized site URL utility for security (env var with fallback)
            const siteUrl = getSiteUrl();

            // Include callId in the redirect URL if present
            const nextUrl = callId
                ? `/calls/${callId}${inviteSuffix}`
                : '/citizen';

            const { error: signUpError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        role: 'citizen',
                        full_name: firstName && lastName ? `${firstName} ${lastName}`.trim() : undefined,
                    },
                    emailRedirectTo: `${siteUrl}/auth/callback?type=citizen&next=${encodeURIComponent(nextUrl)}`,
                },
            });

            if (signUpError) {
                if (signUpError.message.includes('already registered')) {
                    setError('This email is already registered. Please sign in instead.');
                } else {
                    setError(signUpError.message);
                }
                return;
            }

            // Success - show verification message
            setSuccess(true);
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            logger.error('Page', 'Registration error', { err });
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <main className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
                <div className="fixed inset-0 pointer-events-none">
                    <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-600/10 rounded-full blur-[128px]" />
                    <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[128px]" />
                </div>

                <div className="relative z-10 w-full max-w-md p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm text-center">
                    <div className="mb-6 p-4 rounded-full bg-emerald-500/20 w-fit mx-auto">
                        <CheckCircle2 className="text-emerald-400 w-12 h-12" />
                    </div>
                    <h1 className="text-2xl font-bold mb-4">Check Your Email</h1>
                    <p className="text-zinc-400 mb-6">
                        We&apos;ve sent a verification link to <span className="text-white font-medium">{email}</span>.
                        Click the link in the email to verify your account.
                    </p>
                    {hasCallId && (
                        <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30">
                            <div className="flex items-center justify-center gap-3">
                                <Phone className="text-blue-400 w-5 h-5" />
                                <p className="text-blue-200 text-sm">After verifying, you&apos;ll be taken directly to your call</p>
                            </div>
                        </div>
                    )}
                    <p className="text-sm text-zinc-500 mb-8">
                        Didn&apos;t receive the email? Check your spam folder or try registering again.
                    </p>
                    <Link
                        href={callId
                            ? `/citizen?callId=${encodeURIComponent(callId)}${inviteToken ? `&inviteToken=${encodeURIComponent(inviteToken)}` : ''}`
                            : '/citizen'}
                        className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Back to Citizen Portal
                    </Link>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
            {/* Background Ambience */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-600/10 rounded-full blur-[128px]" />
                <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[128px]" />
            </div>

            <div className="relative z-10 w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <Link href="/citizen" className="inline-flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity">
                        <EngageIQLogo theme="dark" width={150} height={41} style={{ width: 'auto', height: '30px' }} />
                        <span className="text-emerald-400 font-normal">Citizen</span>
                    </Link>
                    <h1 className="text-3xl font-bold mb-2">Create Account</h1>
                    <p className="text-zinc-400">Register to track your reports and receive updates</p>
                </div>

                {/* Call Banner - shown when joining via share link */}
                {hasCallId && (
                    <div className="mb-6 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-500/20">
                                <Phone className="text-blue-400 w-5 h-5" />
                            </div>
                            <div className="text-left">
                                <p className="text-blue-200 font-medium">Joining a call</p>
                                <p className="text-blue-300/70 text-sm">Create an account to continue to your call</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm space-y-5">
                    {error && (
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Name fields (optional) */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-zinc-400 mb-2">First Name</label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                                <input
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder="John"
                                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-emerald-500/50 placeholder-zinc-600"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm text-zinc-400 mb-2">Last Name</label>
                            <input
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Doe"
                                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-emerald-500/50 placeholder-zinc-600"
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Email Address *</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-emerald-500/50 placeholder-zinc-600"
                            />
                        </div>
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Password *</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Min. 8 characters"
                                required
                                minLength={8}
                                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-emerald-500/50 placeholder-zinc-600"
                            />
                        </div>
                    </div>

                    {/* Confirm Password */}
                    <div>
                        <label className="block text-sm text-zinc-400 mb-2">Confirm Password *</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 w-5 h-5" />
                            <input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Repeat your password"
                                required
                                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-emerald-500/50 placeholder-zinc-600"
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
                    >
                        {isLoading ? 'Creating Account...' : 'Create Account'}
                        {!isLoading && <ArrowRight size={18} />}
                    </button>

                    {/* Sign in link */}
                    <p className="text-center text-sm text-zinc-400">
                        Already have an account?{' '}
                        <Link
                            href={callId
                                ? `/citizen/login?callId=${encodeURIComponent(callId)}${inviteToken ? `&inviteToken=${encodeURIComponent(inviteToken)}` : ''}`
                                : '/citizen/login'}
                            className="text-emerald-400 hover:text-emerald-300 transition-colors"
                        >
                            Sign in
                        </Link>
                    </p>
                </form>

                {/* Back link */}
                <div className="mt-6 text-center">
                    <Link
                        href={callId
                            ? `/citizen?callId=${encodeURIComponent(callId)}${inviteToken ? `&inviteToken=${encodeURIComponent(inviteToken)}` : ''}`
                            : '/citizen'}
                        className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-300 transition-colors text-sm"
                    >
                        <ArrowLeft size={16} />
                        Back to Citizen Portal
                    </Link>
                </div>
            </div>
        </main>
    );
}

// Main export with Suspense boundary for useSearchParams
export default function CitizenRegister() {
    return (
        <Suspense fallback={
            <main className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
                <div className="text-zinc-400">Loading...</div>
            </main>
        }>
            <CitizenRegisterContent />
        </Suspense>
    );
}
